// DELETE_FLAG: Replace your coinStore.js with this user-specific version:
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import coinService from '../Services/coinService';
import authService from '../Services/authService';

const useCoinStore = create(
  persist(
    (set, get) => ({
      // State
      coinBalance: 0,
      isPro: false,
      proExpiry: null,
      proType: 'monthly',
      loading: false,
      lastSyncTime: null,
      purchaseHistory: [],
      userId: null,

      // Actions
      setLoading: (loading) => set({ loading }),
      
      setBalance: (coinBalance) => set({ 
        coinBalance, 
        lastSyncTime: new Date().toISOString() 
      }),

      // Sync with Firebase (call this on app load and after purchases)
      syncBalance: async () => {
        try {
          set({ loading: true });

          // Wait for Firebase auth to finish initializing before checking user
          await authService.waitForInit();

          // Get current user ID first
          const userId = authService.getCurrentUserIdIfExists();
          
          if (!userId) {
            // No user - reset to 0
            set({
              coinBalance: 0,
              userId: null,
              lastSyncTime: new Date().toISOString(),
              loading: false,
              purchaseHistory: []
            });
            console.log('💰 No user - coin balance reset to 0');
            return 0;
          }

          // Check if this is a different user than what's stored
          const currentStoredUserId = get().userId;
          if (currentStoredUserId && currentStoredUserId !== userId) {
            console.log('🔄 User changed, resetting coin store');
            // Different user - reset store first
            set({
              coinBalance: 0,
              purchaseHistory: [],
              lastSyncTime: null
            });
          }

          const profile = await coinService.getUserProfile();

          set({
            coinBalance: profile.coinBalance,
            isPro: profile.isPro,
            proExpiry: profile.proExpiry,
            proType: profile.proType || 'monthly',
            userId,
            lastSyncTime: new Date().toISOString(),
            loading: false
          });
          
          console.log('💰 Coin balance synced for user:', userId, 'Balance:', profile.coinBalance, 'Pro:', profile.isPro);
          return profile.coinBalance;
        } catch (error) {
          console.error('❌ Failed to sync coin balance:', error);
          set({ loading: false });
          return get().coinBalance; // Return cached balance
        }
      },

      // Add coins to both store and Firebase
      addCoins: async (amount, purchaseDetails = {}) => {
        try {
          // Optimistic update - update UI immediately
          const currentBalance = get().coinBalance;
          set({ coinBalance: currentBalance + amount });

          // Update Firebase
          const result = await coinService.addCoins(amount, purchaseDetails);
          
          if (result.success) {
            // Add to purchase history
            const purchase = {
              amount,
              price: purchaseDetails.price,
              timestamp: new Date().toISOString(),
              paymentId: purchaseDetails.paymentId,
              ...purchaseDetails
            };

            set(state => ({
              purchaseHistory: [...state.purchaseHistory, purchase],
              lastSyncTime: new Date().toISOString()
            }));

            console.log('✅ Coins added successfully:', amount);
            return { success: true, newBalance: result.newBalance };
          } else {
            // Revert optimistic update on failure
            set({ coinBalance: currentBalance });
            console.error('❌ Failed to add coins:', result.error);
            return { success: false, error: result.error };
          }
        } catch (error) {
          // Revert optimistic update on error
          const currentBalance = get().coinBalance;
          set({ coinBalance: currentBalance });
          console.error('❌ Error adding coins:', error);
          return { success: false, error: error.message };
        }
      },

      // Spend coins (with optimistic updates)
      spendCoins: async (amount, purpose = 'room_upgrade', roomId = null) => {
        try {
          const currentBalance = get().coinBalance;
          
          // Check if user has enough coins
          if (currentBalance < amount) {
            return {
              success: false,
              error: 'insufficient_coins',
              message: `Not enough coins. You have ${currentBalance}, need ${amount}.`
            };
          }

          // Optimistic update - deduct coins immediately
          set({ coinBalance: currentBalance - amount });

          // Update Firebase
          const result = await coinService.spendCoins(amount, purpose, roomId);

          if (result.success) {
            console.log('💸 Coins spent successfully:', amount);
            return { success: true, newBalance: result.newBalance };
          } else {
            // Revert optimistic update on failure
            set({ coinBalance: currentBalance });
            console.error('❌ Failed to spend coins:', result.error);
            return { success: false, error: result.error };
          }
        } catch (error) {
          // Revert optimistic update on error
          const currentBalance = get().coinBalance;
          set({ coinBalance: currentBalance });
          console.error('❌ Error spending coins:', error);
          return { success: false, error: error.message };
        }
      },

      // Check if user can afford something
      canAfford: (amount) => {
        return get().coinBalance >= amount;
      },

      // Get purchase history
      getPurchaseHistory: () => {
        return get().purchaseHistory;
      },

      // Grant Pro access (called after successful subscription payment)
      grantPro: (expiry = null, type = 'monthly') => set({ isPro: true, proExpiry: expiry, proType: type }),

      // Revoke Pro (called on cancellation/webhook)
      revokePro: () => set({ isPro: false, proExpiry: null, proType: 'monthly' }),

      // Reset store (for logout or account switching)
      resetStore: () => {
        console.log('🔄 Resetting coin store');
        set({
          coinBalance: 0,
          isPro: false,
          proExpiry: null,
          loading: false,
          lastSyncTime: null,
          purchaseHistory: [],
          userId: null
        });
      },

      // Check if data is stale (older than 5 minutes)
      isStale: () => {
        const lastSync = get().lastSyncTime;
        if (!lastSync) return true;
        
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        return new Date(lastSync) < fiveMinutesAgo;
      }
    }),
    {
      name: 'nameit-coin-store', // localStorage key
      // Only persist essential data, not loading states
      partialize: (state) => ({
        coinBalance: state.coinBalance,
        isPro: state.isPro,
        proExpiry: state.proExpiry,
        proType: state.proType,
        lastSyncTime: state.lastSyncTime,
        purchaseHistory: state.purchaseHistory,
        userId: state.userId
      }),
      // Add version to force reset if needed
      version: 1
    }
  )
);

export default useCoinStore;